import { useMemo, useState } from 'react';
import Avatar from '@/components/base/Avatar';
import { maskAccount } from '@/utils/sendMoney';
import type { SinglePayee } from '@/types/portal';

interface BankRecipientStepProps {
  payees: SinglePayee[];
  onMethod: (method: 'account' | 'phone') => void;
  onPickPayee: (payee: SinglePayee) => void;
}

/** "Please enter the recipient's details" — branch into a bank account or a phone-linked account. */
export default function BankRecipientStep({
  payees,
  onMethod,
  onPickPayee,
}: BankRecipientStepProps) {
  const [search, setSearch] = useState('');

  const results = useMemo(() => {
    const text = search.trim().toLowerCase();
    if (!text) return [];
    return payees
      .filter(
        (item) =>
          item.target.accountNumber &&
          (item.name.toLowerCase().includes(text) ||
            (item.handle ?? '').toLowerCase().includes(text) ||
            (item.target.bankName ?? '').toLowerCase().includes(text)),
      )
      .slice(0, 5);
  }, [search, payees]);

  return (
    <div className="space-y-6">
      <h3 className="font-heading text-lg font-semibold text-foreground-950">
        Please enter the recipient&rsquo;s details
      </h3>

      <ul className="divide-y divide-background-100">
        <li>
          <button
            type="button"
            onClick={() => onMethod('account')}
            className="flex w-full cursor-pointer items-center gap-3 py-4 text-left transition-colors hover:bg-background-100"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 text-base text-secondary-800">
              <i className="ri-bank-line" />
            </span>
            <span className="flex-1 text-sm font-medium text-foreground-900">
              Send to a bank account
            </span>
            <i className="ri-arrow-right-s-line text-foreground-400" />
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={() => onMethod('phone')}
            className="flex w-full cursor-pointer items-center gap-3 py-4 text-left transition-colors hover:bg-background-100"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-base text-primary-700">
              <i className="ri-smartphone-line" />
            </span>
            <span className="flex-1 text-sm font-medium text-foreground-900">
              Send to a phone-linked account
            </span>
            <i className="ri-arrow-right-s-line text-foreground-400" />
          </button>
        </li>
      </ul>

      <div>
        <div className="relative">
          <span className="pointer-events-none absolute left-4 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-base text-foreground-400">
            <i className="ri-search-line" />
          </span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search for a recipient"
            className="h-10 w-full rounded-full border border-background-300 bg-background-50 pl-10 pr-4 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>

        {results.length > 0 ? (
          <ul className="mt-3 divide-y divide-background-100 overflow-hidden rounded-md border border-background-200">
            {results.map((payee) => (
              <li key={payee.id}>
                <button
                  type="button"
                  onClick={() => onPickPayee(payee)}
                  className="flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-background-100"
                >
                  <Avatar src={payee.avatar} alt={payee.name} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground-950">
                      {payee.name}
                    </span>
                    <span className="block truncate text-xs text-foreground-500">
                      {payee.target.bankName} · {maskAccount(payee.target.accountNumber ?? '')}
                    </span>
                  </span>
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}